import { Routes, Route, useNavigate } from "react-router-dom";
// Material-UI imports
import Grid from "@mui/material/Grid";

// MSAL imports
import { MsalProvider, MsalAuthenticationTemplate } from "@azure/msal-react";
import { IPublicClientApplication, InteractionType } from "@azure/msal-browser";
import { CustomNavigationClient } from "./utils/NavigationClient";

// Sample app imports
import { PageLayout, ErrorBoundary } from "./modules/common/components";
import { CssBaseline, Stack } from "@mui/material";
import { Home } from "./modules/home";
import TicketCreate from "./modules/tickets/pages/TicketCreate";
import TicketEdit from "./modules/tickets/pages/TicketEdit";
import TicketsCreatedBy from "./modules/tickets/pages/TicketsCreatedBy";
import TicketsAssignedTo from "./modules/tickets/pages/TicketsAssignedTo";
import TicketsPendingResponse from "./modules/tickets/pages/TicketsPendingResponse";
import TicketsMyTeams from "./modules/tickets/pages/TicketsMyTeams";
import TeamEdit from "./modules/teams/pages/TeamEdit";
import { RecurringTasksList, RecurringTaskCreate, RecurringTaskEdit, RecurringTaskHistory } from "./modules/recurringTasks";
import { WorkflowListPage, WorkflowDetailPage, WorkflowCreatePage } from "./modules/workflows";
import { ApprovalsInboxPage, ApprovalDetailPage } from "./modules/approvals";
import { WorkOrderReportPage, ReportsDirectoryPage, PMReportPage } from "./modules/reports";
import { AssetList, AssetCreate, AssetEdit, PrintAssetLabels } from "./modules/assets";
import { VendorList, VendorCreate, VendorEdit } from "./modules/vendors";
import { ComplianceItemCreate, ComplianceItemEdit, ComplianceDashboard } from "./modules/compliance";
import { GetTheAppPage } from "./modules/mobileApp";
import { ProfilePage } from "./modules/profile";
import { useFeatureFlag } from "./utils/featureFlags";

type AppProps = {
    pca: IPublicClientApplication;
};

function App({ pca }: AppProps) {
    // The next 3 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
    const navigate = useNavigate();
    const navigationClient = new CustomNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);

    return (
        <MsalProvider instance={pca}>
            <CssBaseline />
            <PageLayout>
                    <Grid container justifyContent='center'>
                        <Pages />
                    </Grid>
            </PageLayout>
        </MsalProvider>
    );
}

function Pages() {
    const complianceEnabled = useFeatureFlag('Compliance');

    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/tickets/create" element={<TicketCreate />} />
            <Route path="/tickets/edit" element={<TicketEdit />} />
            <Route path="/tickets/my-tickets" element={<TicketsCreatedBy />} />
            <Route path="/tickets/assigned-to-me" element={<TicketsAssignedTo />} />
            <Route path="/tickets/pending-response" element={<TicketsPendingResponse />} />
            <Route path="/tickets/my-teams" element={<TicketsMyTeams />} />
            <Route path="/teams/edit" element={<TeamEdit />} />
            <Route path="/recurring-tasks" element={<RecurringTasksList />} />
            <Route path="/recurring-tasks/create" element={<RecurringTaskCreate />} />
            <Route path="/recurring-tasks/edit" element={<RecurringTaskEdit />} />
            <Route path="/recurring-tasks/history" element={<RecurringTaskHistory />} />
            <Route path="/workflows" element={<ErrorBoundary moduleName="Workflows"><WorkflowListPage /></ErrorBoundary>} />
            <Route path="/workflows/create" element={<ErrorBoundary moduleName="Workflows"><WorkflowCreatePage /></ErrorBoundary>} />
            <Route path="/workflows/:id" element={<ErrorBoundary moduleName="Workflows"><WorkflowDetailPage /></ErrorBoundary>} />
            <Route path="/approvals" element={
                <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
                    <ErrorBoundary moduleName="Approvals"><ApprovalsInboxPage /></ErrorBoundary>
                </MsalAuthenticationTemplate>
            } />
            <Route path="/approvals/:approvalRequestId" element={
                <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
                    <ErrorBoundary moduleName="Approvals"><ApprovalDetailPage /></ErrorBoundary>
                </MsalAuthenticationTemplate>
            } />
            <Route path="/reports" element={<ReportsDirectoryPage />} />
            <Route path="/reports/work-orders" element={<WorkOrderReportPage />} />
            <Route path="/reports/pm" element={<PMReportPage />} />
            <Route path="/assets" element={<AssetList />} />
            <Route path="/assets/create" element={<AssetCreate />} />
            <Route path="/assets/edit" element={<AssetEdit />} />
            <Route path="/assets/print-labels" element={<PrintAssetLabels />} />
            <Route path="/vendors" element={<VendorList />} />
            <Route path="/vendors/create" element={<VendorCreate />} />
            <Route path="/vendors/edit" element={<VendorEdit />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/get-the-app" element={
                <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
                    <ErrorBoundary moduleName="Mobile App"><GetTheAppPage /></ErrorBoundary>
                </MsalAuthenticationTemplate>
            } />
            {complianceEnabled && (
                <>
                    <Route path="/compliance/create" element={<ComplianceItemCreate />} />
                    <Route path="/compliance/edit" element={<ComplianceItemEdit />} />
                    <Route path="/compliance" element={<ComplianceDashboard />} />
                </>
            )}
        </Routes>
    );
}

export default App;